import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Plus, Trash2, Edit2, Check, X, Bot, Database, Layers, FileText, LayoutTemplate } from 'lucide-react'
import { aiGeneratorAPI, categoryAPI } from '../../services/api'
import OccasionScheduler from '../../components/OccasionScheduler/OccasionScheduler'

const KNOWLEDGEBASE_TYPES = [
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'instruction', label: 'Instruction' },
  { value: 'rule', label: 'Rule' },
]

const FIELD_TYPES = [
  // AI-generated types
  { value: 'text', label: 'Text (AI)' },
  { value: 'image', label: 'Image (AI)' },
  { value: 'music', label: 'Music (AI)' },
  { value: 'song', label: 'Song (AI)' },
  // Static runtime types: values are provided manually when running the generator
  { value: 'static_runtime_text', label: 'Static Text (manual at run)' },
  { value: 'static_runtime_url', label: 'Static URL (manual at run)' },
  { value: 'static_runtime_image', label: 'Static Image (manual at run)' },
]

const MAX_TEMPLATES = 25

const AIGeneratorFormPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = Boolean(id)

  const [activeTab, setActiveTab] = useState('details')

  // Generator form state
  const [form, setForm] = useState({ name: '', description: '', requires_approval: false })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Generator-level default Occasion Scheduling
  const [generatorOccasion, setGeneratorOccasion] = useState(null)
  const occasionSchedulerRef = useRef(null)

  // Categories for the design-template category/subcategory selectors.
  // categories        = top-level (parent) categories, loaded once.
  // categoriesById    = categoryId -> name (for display / fallback).
  // subCatsByCategory = categoryId -> [subcategory,...] (lazy-loaded).
  const [categories, setCategories] = useState([])
  const [categoriesById, setCategoriesById] = useState({})
  const [subCatsByCategory, setSubCatsByCategory] = useState({})

  // Knowledgebase state
  const [knowledgebase, setKnowledgebase] = useState([])
  const [kbForm, setKbForm] = useState({ content: '', type: 'knowledge' })
  const [editingKbId, setEditingKbId] = useState(null)
  const [editingKbForm, setEditingKbForm] = useState({ content: '', type: 'knowledge' })

  // Fields state
  const [fields, setFields] = useState([])
  const [fieldForm, setFieldForm] = useState({ field_name: '', field_description: '', field_type: 'text' })
  const [editingFieldId, setEditingFieldId] = useState(null)
  const [editingFieldForm, setEditingFieldForm] = useState({ field_name: '', field_description: '', field_type: 'text' })

  // Design templates state
  const [templates, setTemplates] = useState([])
  const [tplForm, setTplForm] = useState({ template_id: '', tag: '' })
  const [editingTplId, setEditingTplId] = useState(null)
  const [editingTplForm, setEditingTplForm] = useState({ template_id: '', tag: '' })

  useEffect(() => {
    if (isEditing) {
      fetchGenerator()
    }
  }, [id])

  // Load top-level (parent) categories once, for the design-template category selector.
  useEffect(() => {
    (async () => {
      try {
        const response = await categoryAPI.getAll({ type: 'template', isActive: true, parentId: '' })
        if (response.data.success) {
          const list = (response.data.data || []).sort((a, b) =>
            String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase())
          )
          setCategories(list)
          setCategoriesById(prev => {
            const next = { ...prev }
            list.forEach(c => { next[c.id] = c.name })
            return next
          })
        }
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    })()
  }, [])

  const fetchGenerator = async () => {
    try {
      setLoading(true)
      const response = await aiGeneratorAPI.getById(id)
      if (response.data.status === 'success') {
        const data = response.data.data
        setForm({ name: data.name, description: data.description || '', requires_approval: !!data.requiresApproval })
        setGeneratorOccasion(data.occasion || null)
        setKnowledgebase(data.knowledgebase || [])
        setFields(data.fields || [])
        const tpls = data.templates || []
        setTemplates(tpls)
        // Preload subcategory options for each template's category so the
        // subcategory dropdown is populated immediately.
        const uniqueCats = [...new Set(tpls.map(t => t.categoryId).filter(Boolean))]
        uniqueCats.forEach(catId => { loadSubCategories(catId); loadCategoryName(catId) })
      }
    } catch (error) {
      console.error('Error fetching generator:', error)
      alert('Failed to load generator')
      navigate('/dashboard/ai-generator')
    } finally {
      setLoading(false)
    }
  }

  // Load subcategories (child categories) for a given category id, cached.
  const loadSubCategories = async (categoryId) => {
    if (!categoryId || subCatsByCategory[categoryId]) return
    try {
      const response = await categoryAPI.getAll({ type: 'template', isActive: true, parentId: categoryId })
      if (response.data.success) {
        const subs = (response.data.data || []).sort((a, b) =>
          String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase())
        )
        setSubCatsByCategory(prev => ({ ...prev, [categoryId]: subs }))
      }
    } catch (error) {
      console.error('Error loading subcategories:', error)
    }
  }

  // Look up and cache a category's display name.
  const loadCategoryName = async (categoryId) => {
    if (!categoryId || categoriesById[categoryId]) return
    try {
      const response = await categoryAPI.getById(categoryId)
      const cat = response.data?.data
      if (cat) setCategoriesById(prev => ({ ...prev, [categoryId]: cat.name }))
    } catch (error) {
      /* non-fatal — fall back to showing the id */
    }
  }

  const handleSaveGenerator = async () => {
    if (!form.name.trim()) {
      alert('Name is required')
      return
    }
    // Collect the generator-level default occasion config; validate if enabled.
    let occasion = generatorOccasion
    if (occasionSchedulerRef.current) {
      const cfg = occasionSchedulerRef.current.getConfig()
      if (cfg?.isOccasionDateApplicable && !occasionSchedulerRef.current.validate()) {
        alert('Please fix the occasion scheduling configuration errors')
        return
      }
      occasion = cfg
    }
    try {
      setSaving(true)
      const payload = { ...form, occasion }
      if (isEditing) {
        await aiGeneratorAPI.update(id, payload)
        alert('Saved successfully')
      } else {
        const response = await aiGeneratorAPI.create(payload)
        if (response.data.status === 'success') {
          navigate(`/dashboard/ai-generator/${response.data.data.id}`)
          return
        }
      }
    } catch (error) {
      console.error('Error saving generator:', error)
      alert('Failed to save')
    } finally {
      setSaving(false)
    }
  }

  // ==================== Knowledgebase Handlers ====================

  const handleAddKb = async () => {
    if (!kbForm.content.trim()) return
    try {
      const response = await aiGeneratorAPI.createKnowledgebase(id, kbForm)
      if (response.data.status === 'success') {
        setKnowledgebase(prev => [...prev, response.data.data])
        setKbForm({ content: '', type: 'knowledge' })
      }
    } catch (error) {
      alert('Failed to add entry')
    }
  }

  const handleUpdateKb = async (kbId) => {
    try {
      const response = await aiGeneratorAPI.updateKnowledgebase(kbId, editingKbForm)
      if (response.data.status === 'success') {
        setKnowledgebase(prev => prev.map(kb => kb.id === kbId ? response.data.data : kb))
        setEditingKbId(null)
      }
    } catch (error) {
      alert('Failed to update entry')
    }
  }

  const handleDeleteKb = async (kbId) => {
    if (!window.confirm('Delete this knowledgebase entry?')) return
    try {
      await aiGeneratorAPI.deleteKnowledgebase(kbId)
      setKnowledgebase(prev => prev.filter(kb => kb.id !== kbId))
    } catch (error) {
      alert('Failed to delete entry')
    }
  }

  // ==================== Fields Handlers ====================

  const handleAddField = async () => {
    if (!fieldForm.field_name.trim()) return
    try {
      const response = await aiGeneratorAPI.createField(id, fieldForm)
      if (response.data.status === 'success') {
        setFields(prev => [...prev, response.data.data])
        setFieldForm({ field_name: '', field_description: '', field_type: 'text' })
      }
    } catch (error) {
      alert('Failed to add field')
    }
  }

  const handleUpdateField = async (fieldId) => {
    try {
      const response = await aiGeneratorAPI.updateField(fieldId, editingFieldForm)
      if (response.data.status === 'success') {
        setFields(prev => prev.map(f => f.id === fieldId ? response.data.data : f))
        setEditingFieldId(null)
      }
    } catch (error) {
      alert('Failed to update field')
    }
  }

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Delete this field?')) return
    try {
      await aiGeneratorAPI.deleteField(fieldId)
      setFields(prev => prev.filter(f => f.id !== fieldId))
    } catch (error) {
      alert('Failed to delete field')
    }
  }

  // ==================== Design Template Handlers ====================

  const handleAddTemplate = async () => {
    if (!tplForm.template_id.trim()) return
    if (templates.length >= MAX_TEMPLATES) {
      alert(`Maximum ${MAX_TEMPLATES} design templates allowed`)
      return
    }
    try {
      const response = await aiGeneratorAPI.createTemplate(id, tplForm)
      if (response.data.status === 'success') {
        const added = response.data.data
        setTemplates(prev => [...prev, added])
        setTplForm({ template_id: '', tag: '' })
        // Prefetched category comes back on the entry — load its subcategory
        // options + display name so the selector is ready immediately.
        if (added.categoryId) {
          loadSubCategories(added.categoryId)
          loadCategoryName(added.categoryId)
        }
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add template')
    }
  }

  // Set/clear the category on a design-template entry. Changing the category
  // clears any previously-set subcategory (it belonged to the old category).
  const handleSetTemplateCategory = async (tplId, categoryId) => {
    try {
      const response = await aiGeneratorAPI.updateTemplate(tplId, {
        category_id: categoryId || null,
        sub_category_id: null,
      })
      if (response.data.status === 'success') {
        setTemplates(prev => prev.map(t => t.id === tplId ? response.data.data : t))
        if (categoryId) loadSubCategories(categoryId)
      }
    } catch (error) {
      alert('Failed to update category')
    }
  }

  // Set/clear the sub-category on a design-template entry.
  const handleSetTemplateSubcategory = async (tplId, subCategoryId) => {
    try {
      const response = await aiGeneratorAPI.updateTemplate(tplId, { sub_category_id: subCategoryId || null })
      if (response.data.status === 'success') {
        setTemplates(prev => prev.map(t => t.id === tplId ? response.data.data : t))
      }
    } catch (error) {
      alert('Failed to update subcategory')
    }
  }

  const handleUpdateTemplate = async (tplId) => {
    try {
      const response = await aiGeneratorAPI.updateTemplate(tplId, editingTplForm)
      if (response.data.status === 'success') {
        setTemplates(prev => prev.map(t => t.id === tplId ? response.data.data : t))
        setEditingTplId(null)
      }
    } catch (error) {
      alert('Failed to update template')
    }
  }

  const handleDeleteTemplate = async (tplId) => {
    if (!window.confirm('Remove this design template?')) return
    try {
      await aiGeneratorAPI.deleteTemplate(tplId)
      setTemplates(prev => prev.filter(t => t.id !== tplId))
    } catch (error) {
      alert('Failed to delete template')
    }
  }

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>
  }

  const tabs = [
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'knowledgebase', label: 'Knowledgebase', icon: Database, count: knowledgebase.length, disabled: !isEditing },
    { id: 'fields', label: 'Fields', icon: Layers, count: fields.length, disabled: !isEditing },
    { id: 'templates', label: 'Design Templates', icon: LayoutTemplate, count: templates.length, disabled: !isEditing },
  ]

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/dashboard/ai-generator')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-purple-600" />
            {isEditing ? (form.name || 'Edit AI Generator') : 'Create AI Generator'}
          </h1>
          {isEditing && form.description && (
            <p className="text-sm text-gray-500 mt-0.5">{form.description}</p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {tabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              disabled={tab.disabled}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-700'
                  : tab.disabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {typeof tab.count === 'number' && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === tab.id ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Good Morning Design Generator"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this AI generator does..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Approval toggle */}
            <div className="flex items-start justify-between p-4 bg-amber-50 border border-amber-100 rounded-lg">
              <div className="pr-4">
                <label className="text-sm font-medium text-gray-800">Require Approval</label>
                <p className="text-xs text-gray-500 mt-0.5">
                  When ON, each execution waits for manual approval after content is generated.
                  The design template is created only after you Approve. Deny discards it.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, requires_approval: !prev.requires_approval }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${form.requires_approval ? 'bg-amber-500' : 'bg-gray-300'}`}
                role="switch"
                aria-checked={form.requires_approval}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.requires_approval ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Default Occasion Scheduling — applied to every template produced by
                a run (a run can still override it in the Run modal). */}
            <div className="pt-2">
              <OccasionScheduler ref={occasionSchedulerRef} value={generatorOccasion || {}} />
              <p className="text-xs text-gray-500 mt-2">
                This default schedule is saved on the generator and applied to every template it generates.
                You can still override it when running the generator.
              </p>
            </div>

            {!isEditing && (
              <p className="text-xs text-gray-400">
                Save the generator first, then add knowledgebase, fields, and design templates in their tabs.
              </p>
            )}
            <div className="flex justify-end">
              <button
                onClick={handleSaveGenerator}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Generator'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'knowledgebase' && isEditing && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-sm text-gray-500 mb-4">
            Add knowledge, instructions, and rules that guide the AI generation.
          </p>

          <div className="flex gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
            <textarea
              value={kbForm.content}
              onChange={(e) => setKbForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Enter knowledge content..."
              rows={2}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <div className="flex flex-col gap-2">
              <select
                value={kbForm.type}
                onChange={(e) => setKbForm(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {KNOWLEDGEBASE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <button onClick={handleAddKb} className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {knowledgebase.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No knowledgebase entries yet</p>
            ) : (
              knowledgebase.map((kb) => (
                <div key={kb.id} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                  {editingKbId === kb.id ? (
                    <>
                      <textarea
                        value={editingKbForm.content}
                        onChange={(e) => setEditingKbForm(prev => ({ ...prev, content: e.target.value }))}
                        rows={2}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <select
                        value={editingKbForm.type}
                        onChange={(e) => setEditingKbForm(prev => ({ ...prev, type: e.target.value }))}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        {KNOWLEDGEBASE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <button onClick={() => handleUpdateKb(kb.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingKbId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1"><p className="text-sm text-gray-800">{kb.content}</p></div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        kb.type === 'knowledge' ? 'bg-blue-100 text-blue-700' :
                        kb.type === 'instruction' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>{kb.type}</span>
                      <button onClick={() => { setEditingKbId(kb.id); setEditingKbForm({ content: kb.content, type: kb.type }) }} className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteKb(kb.id)} className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'fields' && isEditing && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-sm text-gray-500 mb-4">
            Define the fields the AI should generate values for (e.g., quote, date, background image prompt).
          </p>

          <div className="grid grid-cols-12 gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
            <input
              type="text"
              value={fieldForm.field_name}
              onChange={(e) => setFieldForm(prev => ({ ...prev, field_name: e.target.value }))}
              placeholder="Field name (e.g., gm_quote)"
              className="col-span-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <input
              type="text"
              value={fieldForm.field_description}
              onChange={(e) => setFieldForm(prev => ({ ...prev, field_description: e.target.value }))}
              placeholder="Description"
              className="col-span-5 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <select
              value={fieldForm.field_type}
              onChange={(e) => setFieldForm(prev => ({ ...prev, field_type: e.target.value }))}
              className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button onClick={handleAddField} className="col-span-2 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
              <Plus className="w-3 h-3" /> Add Field
            </button>
          </div>

          <div className="space-y-2">
            {fields.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No fields defined yet</p>
            ) : (
              fields.map((field) => (
                <div key={field.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                  {editingFieldId === field.id ? (
                    <>
                      <input type="text" value={editingFieldForm.field_name} onChange={(e) => setEditingFieldForm(prev => ({ ...prev, field_name: e.target.value }))} className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm" />
                      <input type="text" value={editingFieldForm.field_description} onChange={(e) => setEditingFieldForm(prev => ({ ...prev, field_description: e.target.value }))} className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm" />
                      <select value={editingFieldForm.field_type} onChange={(e) => setEditingFieldForm(prev => ({ ...prev, field_type: e.target.value }))} className="px-2 py-1.5 border border-gray-300 rounded text-sm">
                        {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <button onClick={() => handleUpdateField(field.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingFieldId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{field.fieldName}</span>
                        {field.fieldDescription && <span className="text-sm text-gray-500 ml-2">- {field.fieldDescription}</span>}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        String(field.fieldType).startsWith('static_runtime_') ? 'bg-emerald-100 text-emerald-700' :
                        field.fieldType === 'text' ? 'bg-gray-100 text-gray-700' :
                        field.fieldType === 'image' ? 'bg-purple-100 text-purple-700' :
                        field.fieldType === 'music' ? 'bg-pink-100 text-pink-700' : 'bg-indigo-100 text-indigo-700'
                      }`}>{field.fieldType}</span>
                      <button onClick={() => { setEditingFieldId(field.id); setEditingFieldForm({ field_name: field.fieldName, field_description: field.fieldDescription || '', field_type: field.fieldType }) }} className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteField(field.id)} className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'templates' && isEditing && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              Add design template IDs to fill with generated values. Running the generator triggers one execution per template (max {MAX_TEMPLATES}), all counted as one.
            </p>
            <span className="text-xs text-gray-400 whitespace-nowrap ml-3">{templates.length}/{MAX_TEMPLATES}</span>
          </div>

          <div className="grid grid-cols-12 gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
            <input
              type="text"
              value={tplForm.template_id}
              onChange={(e) => setTplForm(prev => ({ ...prev, template_id: e.target.value }))}
              placeholder="Design template ID"
              className="col-span-6 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <input
              type="text"
              value={tplForm.tag}
              onChange={(e) => setTplForm(prev => ({ ...prev, tag: e.target.value }))}
              placeholder="Tag (optional, e.g., portrait)"
              className="col-span-4 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              onClick={handleAddTemplate}
              disabled={templates.length >= MAX_TEMPLATES}
              className="col-span-2 flex items-center justify-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>

          {templates.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No design templates added yet</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                  <th className="py-2 px-2">Template ID</th>
                  <th className="py-2 px-2">Tag</th>
                  <th className="py-2 px-2">Category / Subcategory</th>
                  <th className="py-2 px-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {templates.map((tpl) => (
                  <tr key={tpl.id}>
                    {editingTplId === tpl.id ? (
                      <>
                        <td className="py-2 px-2">
                          <input type="text" value={editingTplForm.template_id} onChange={(e) => setEditingTplForm(prev => ({ ...prev, template_id: e.target.value }))} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                        </td>
                        <td className="py-2 px-2">
                          <input type="text" value={editingTplForm.tag} onChange={(e) => setEditingTplForm(prev => ({ ...prev, tag: e.target.value }))} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-400">Set category after saving</td>
                        <td className="py-2 px-2 text-right whitespace-nowrap">
                          <button onClick={() => handleUpdateTemplate(tpl.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingTplId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 px-2">
                          <span className="text-sm font-mono text-gray-800">{tpl.templateId}</span>
                        </td>
                        <td className="py-2 px-2">
                          {tpl.tag ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">{tpl.tag}</span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          {/* Category defaults to the template's prefetched category but is
                              editable, so a subcategory can always be set — even when the
                              source template had no category. */}
                          <div className="flex flex-col gap-1 max-w-[240px]">
                            <div>
                              <span className="text-[10px] text-gray-400 uppercase tracking-wide">Category</span>
                              <select
                                value={tpl.categoryId || ''}
                                onChange={(e) => handleSetTemplateCategory(tpl.id, e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              >
                                <option value="">No category</option>
                                {/* Keep the prefetched category visible even if it isn't a
                                    top-level template category in the loaded list. */}
                                {tpl.categoryId && !categories.some(c => c.id === tpl.categoryId) && (
                                  <option value={tpl.categoryId}>
                                    {categoriesById[tpl.categoryId] || tpl.categoryId}
                                  </option>
                                )}
                                {categories.map(c => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <span className="text-[10px] text-gray-400 uppercase tracking-wide">Subcategory</span>
                              <select
                                value={tpl.subCategoryId || ''}
                                disabled={!tpl.categoryId}
                                onFocus={() => tpl.categoryId && loadSubCategories(tpl.categoryId)}
                                onChange={(e) => handleSetTemplateSubcategory(tpl.id, e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                              >
                                <option value="">{tpl.categoryId ? 'No subcategory' : 'Select a category first'}</option>
                                {(subCatsByCategory[tpl.categoryId] || []).map(sc => (
                                  <option key={sc.id} value={sc.id}>{sc.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right whitespace-nowrap">
                          <a href={`/editor/${tpl.templateId}`} target="_blank" rel="noopener noreferrer" className="p-1 text-gray-500 hover:text-purple-600 inline-flex" title="Open in editor">
                            <LayoutTemplate className="w-4 h-4" />
                          </a>
                          <button onClick={() => { setEditingTplId(tpl.id); setEditingTplForm({ template_id: tpl.templateId, tag: tpl.tag || '' }) }} className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteTemplate(tpl.id)} className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

export default AIGeneratorFormPage
